import React, { useCallback, useEffect, useState } from 'react';
import { Button, FormInput } from 'modules';
import { Col, Modal, Row } from 'react-bootstrap';
import { useMutation } from '@apollo/react-hooks';
import { notifyFailed, notifySuccess } from 'helpers/notify';
import { CompareSignatureMutation, CreateSignatureMutation } from 'queries/Signature';

interface SignWithSmsFormData {
  signCode?: string;
}

interface SignatureInForm {
  id: string;
}

interface SignWithSmsProps {
  phone: string;
  title?: string;
  labelSignFor?: string;
  labelSubmit?: string;
  onSubmit: (signature: GQL.ISignature) => void;
  onClose: () => void;
}

const SignWithSms = ({
  title = 'Authorizácia',
  phone,
  labelSignFor = 'autorizáciu',
  labelSubmit = 'Autorizovať',
  onSubmit,
  onClose,
}: SignWithSmsProps) => {
  const signCodeInput = React.useRef<any>(null);
  const [values, setValues] = useState<SignWithSmsFormData>({ signCode: '' });
  const [errors, setErrors] = useState<SignWithSmsFormData>({ signCode: '' });
  const [enableResendSMS, setEnableResendSMS] = useState(false);

  const [codeSended, setCodeSended] = useState<boolean>(false);
  const [signature, setSignature] = useState<SignatureInForm | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [createSignCode] = useMutation(CreateSignatureMutation);
  const [compareSignCode] = useMutation(CompareSignatureMutation);

  // eslint-disable-next-line
  let counter: any = null;

  const isValid = (): boolean => {
    const newErrors = { ...errors };
    newErrors.signCode = values.signCode === '' ? 'Kód pre podpis je povinný!' : '';
    setErrors(newErrors);
    signCodeInput?.current?.focus();
    // chybová hláška
    return newErrors.signCode === '' && !!signature;
  };

  const handleSubmitSignCode = () => {
    if (isValid() && signature) {
      setLoading(true);
      compareSignCode({
        variables: {
          data: {
            id: signature.id,
            testCode: values.signCode,
          },
        },
      })
        .then((result) => result.data.compareSignature)
        .then((data) => onSubmit(data))
        .catch(() => {
          setErrors({
            ...errors,
            signCode: 'Zadaný kód nie je správny',
          });
          notifyFailed('Zadaný kód nie je správny');
        });
    }
  };

  const onKeyPress = (e: React.KeyboardEvent) => {
    if (e?.key?.toLowerCase() === 'enter') {
      handleSubmitSignCode();
      e.preventDefault();
    }
  };

  const handleChange = (e: React.ChangeEvent) => {
    setValues({
      signCode: (e.target as HTMLInputElement).value,
    });
  };

  const handleSendSMS = useCallback(() => {
    setEnableResendSMS(false);
    createSignCode({
      variables: {
        data: {
          phone,
        },
      },
    })
      .then((result) => result.data.createSignature)
      .then((data) => {
        notifySuccess('SMS bola odoslaná');
        setCodeSended(true);
        setSignature(data);
        setErrors({});
        signCodeInput?.current?.focus();
      })
      .finally(() => {
        // eslint-disable-next-line
        counter = setTimeout(() => setEnableResendSMS(true), 30*1000);
      });
  }, []);

  useEffect(() => handleSendSMS(), [handleSendSMS]);

  return (
    <Modal show backdropClassName="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxWidth: 400 }}>
        <p>
          Na váš telefón <strong>{phone}</strong> bol odoslaný kód pre {labelSignFor}.
          <br />
          Zadajte kód z SMS a stlačte tlačidlo "{labelSubmit}".
        </p>
        <FormInput
          ref={signCodeInput}
          name="signCode"
          autoFocus
          placeholder="Sem opíšte kód s SMS správy"
          value={values.signCode}
          error={errors.signCode}
          disabled={!codeSended || loading}
          style={{
            textAlign: 'center',
            padding: 17,
            marginRight: 5,
            marginBottom: 0,
          }}
          autoComplete="off"
          onChange={handleChange}
          onKeyPress={onKeyPress}
        />
        <Row>
          <Col xs={12} className="mt-2">
            <Button
              variant="link"
              disabled={!enableResendSMS || loading}
              style={{ padding: 0, textDecoration: 'underline', fontSize: '90%' }}
              onClick={handleSendSMS}
            >
              Poslať novú SMS
            </Button>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="danger" loading={loading} disabled={!codeSended || loading} onClick={handleSubmitSignCode}>
          {labelSubmit}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SignWithSms;
